<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('namingConverter.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('namingConverter.title') }}</h1>
      <p>{{ $t('namingConverter.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="editor-panel">
        <label>{{ $t('namingConverter.inputLabel') }}</label>
        <textarea
          v-model="input"
          :placeholder="$t('namingConverter.inputPlaceholder')"
          spellcheck="false"
          @input="convert"
        ></textarea>
      </div>

      <div class="results-grid">
        <div class="result-item" v-for="item in results" :key="item.key">
          <div class="result-header">
            <span class="result-label">{{ item.label }}</span>
            <button class="copy-btn" @click="copy(item.value)" :title="$t('common.copy')">📋</button>
          </div>
          <div class="result-value">{{ item.value || '-' }}</div>
        </div>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('namingConverter.whatIs') }}</h2>
      <p>{{ $t('namingConverter.whatIsDesc') }}</p>

      <h2>{{ $t('namingConverter.stylesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('namingConverter.styles')" :key="i">{{ item }}</li>
      </ul>

      <h2>{{ $t('namingConverter.tipsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('namingConverter.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NamingConverter',
  data() {
    return {
      input: '',
      results: [],
      successMessage: ''
    }
  },
  methods: {
    // 将输入文本拆分为单词数组
    splitWords(str) {
      if (!str.trim()) return []
      // 先处理常见分隔符
      let s = str.trim()
      // 如果包含空格、下划线、中划线，按分隔符拆分
      if (/[\s_\-.]/.test(s)) {
        return s.split(/[\s_\-.]+/).filter(w => w.length > 0).map(w => w.toLowerCase())
      }
      // 按大小写边界拆分 (camelCase / PascalCase)
      return s
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .split(/\s+/)
        .filter(w => w.length > 0)
        .map(w => w.toLowerCase())
    },
    toCamelCase(words) {
      if (words.length === 0) return ''
      return words[0] + words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')
    },
    toPascalCase(words) {
      return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')
    },
    toSnakeCase(words) {
      return words.join('_')
    },
    toKebabCase(words) {
      return words.join('-')
    },
    toConstantCase(words) {
      return words.map(w => w.toUpperCase()).join('_')
    },
    toDotCase(words) {
      return words.join('.')
    },
    toPathCase(words) {
      return words.join('/')
    },
    toTitleCase(words) {
      return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    },
    toSentenceCase(words) {
      if (words.length === 0) return ''
      return words[0].charAt(0).toUpperCase() + words[0].slice(1) + (words.length > 1 ? ' ' + words.slice(1).join(' ') : '')
    },
    convert() {
      const words = this.splitWords(this.input)
      this.results = [
        { key: 'camel', label: 'camelCase', value: this.toCamelCase(words) },
        { key: 'pascal', label: 'PascalCase', value: this.toPascalCase(words) },
        { key: 'snake', label: 'snake_case', value: this.toSnakeCase(words) },
        { key: 'kebab', label: 'kebab-case', value: this.toKebabCase(words) },
        { key: 'constant', label: 'CONSTANT_CASE', value: this.toConstantCase(words) },
        { key: 'dot', label: 'dot.case', value: this.toDotCase(words) },
        { key: 'path', label: 'path/case', value: this.toPathCase(words) },
        { key: 'title', label: 'Title Case', value: this.toTitleCase(words) },
        { key: 'sentence', label: 'Sentence case', value: this.toSentenceCase(words) }
      ]
    },
    async copy(text) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        this.successMessage = this.$t('common.copied')
      } catch {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.successMessage = this.$t('common.copied')
      }
      setTimeout(() => { this.successMessage = '' }, 2000)
    }
  },
  mounted() {
    this.convert()
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.editor-panel { margin-bottom: 20px; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: block; }
.editor-panel textarea { min-height: 80px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 14px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; transition: border-color 0.3s; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.results-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.result-item { background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 10px; padding: 14px 16px; }
.result-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.result-label { font-size: 12px; color: var(--text-secondary); font-weight: 600; text-transform: none; }
.result-value { font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 15px; color: var(--text-primary); word-break: break-all; min-height: 22px; }
.copy-btn { background: none; border: none; cursor: pointer; font-size: 14px; padding: 2px 6px; border-radius: 4px; transition: background 0.2s; }
.copy-btn:hover { background: var(--bg-hover); }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-top: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
@media (max-width: 768px) {
  .results-grid { grid-template-columns: 1fr; }
}
</style>
